// Course API service

import { API_BASE_URL } from '@/config/env'
import { apiRequest } from '@/services/api/interceptor';
import type {
  SaveCourseRequest,
  UserCourseHistoryResponse,
  GetMyCoursesResponse,
  ClarificationsResponse,
  ClarificationAnswers,
} from './types';


/**
 * Save a course to user's library
 */
export async function saveCourse(
  request: SaveCourseRequest
): Promise<{ success: boolean; data: UserCourseHistoryResponse; message: string }> {
  const url = `${API_BASE_URL}/api/core/courses/save`;

  return apiRequest(url, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Get user's saved courses
 */
export async function getMyCourses(
  status?: string,
  isGenerated?: boolean
): Promise<GetMyCoursesResponse> {
  let url = `${API_BASE_URL}/api/core/courses/my-courses`;

  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (isGenerated !== undefined) params.append('isGenerated', String(isGenerated));

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  return apiRequest(url, {
    method: 'GET',
  });
}

/**
 * Delete a saved course
 */
export async function deleteSavedCourse(courseId: string): Promise<{ success: boolean; message: string }> {
  const url = `${API_BASE_URL}/api/core/courses/my-courses/${courseId}`;

  return apiRequest(url, {
    method: 'DELETE',
  });
}

/**
 * Archive a saved course (sets status to DROPPED, can be unarchived later)
 */
export async function archiveCourse(courseId: string): Promise<{ success: boolean; message: string }> {
  const url = `${API_BASE_URL}/api/core/courses/my-courses/${courseId}/archive`;

  return apiRequest(url, {
    method: 'PATCH',
  });
}

/**
 * Unarchive a course (restores status to ACTIVE)
 */
export async function unarchiveCourse(courseId: string): Promise<{ success: boolean; message: string }> {
  const url = `${API_BASE_URL}/api/core/courses/my-courses/${courseId}/unarchive`;

  return apiRequest(url, {
    method: 'PATCH',
  });
}

// =============================================================================
// Course Generation Job Types
// =============================================================================

export interface CourseGenerationJob {
  jobId: string;
  userId: string;
  tenantId: string;
  courseTopic: string;
  experience: string;
  hobbies: string;
  language: string;
  status: 'QUEUED' | 'CONNECTING' | 'GENERATING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  progress: number;
  currentStep: string;
  errorMessage?: string;
  courseId?: string;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  updatedAt: string;
}

// =============================================================================
// Course Generation Job API Functions
// =============================================================================

/**
 * Get user's course generation jobs (for restoring state after page refresh)
 */
export async function getGenerationJobs(limit: number = 10): Promise<CourseGenerationJob[]> {
  const url = `${API_BASE_URL}/api/core/courses/generate/jobs?limit=${limit}`;

  return apiRequest(url, {
    method: 'GET',
  });
}

/**
 * Get a specific generation job by ID
 */
export async function getGenerationJob(jobId: string): Promise<CourseGenerationJob> {
  const url = `${API_BASE_URL}/api/core/courses/generate/jobs/${jobId}`;

  return apiRequest(url, {
    method: 'GET',
  });
}

/**
 * Cancel a course generation job
 */
export async function cancelGenerationJob(jobId: string): Promise<{ success: boolean; message: string }> {
  const url = `${API_BASE_URL}/api/core/courses/generate/jobs/${jobId}`;

  return apiRequest(url, {
    method: 'DELETE',
  });
}

/**
 * Check if user already has a course with a similar topic
 */
export async function checkDuplicateTopic(
  topic: string
): Promise<{ isDuplicate: boolean; matchedTopic: string | null }> {
  const url = `${API_BASE_URL}/api/core/courses/check-duplicate?topic=${encodeURIComponent(topic)}`;

  return apiRequest(url, {
    method: 'GET',
  });
}

// =============================================================================
// Pre-Generation Clarifying Quiz
// =============================================================================

export interface ClarificationsRequest {
  topic: string;
  difficulty?: string;
  /** Things the user says they already know. Sent to backend as snake_case. */
  existingKnowledge: string[];
  hobbies?: string[];
}

/**
 * Fetch up to 3 clarifying self-rating questions before course generation.
 *
 * The backend always returns a `questions` array — an empty array means
 * "skip the quiz" (priors unrelated, below target level, or ai-service
 * unavailable). Callers should never need to distinguish skip from failure.
 */
export async function getClarifications(
  request: ClarificationsRequest
): Promise<ClarificationsResponse> {
  const url = `${API_BASE_URL}/api/core/courses/generate/clarifications`;

  return apiRequest(url, {
    method: 'POST',
    body: JSON.stringify({
      topic: request.topic,
      difficulty: request.difficulty ?? 'intermediate',
      existing_knowledge: request.existingKnowledge,
      hobbies: request.hobbies ?? [],
    }),
  });
}

export interface PrepareGenerationRequest {
  courseTopic: string;
  existingKnowledge?: string[];
  hobbiesInterests?: string[];
  preferredLanguage?: string;
  difficultyLevel?: 'beginner' | 'intermediate' | 'advanced';
  clarifications?: ClarificationAnswers;
  clarificationsFreeform?: string;
}

/**
 * Prepare a course generation job. Stores the full payload server-side
 * (10-min TTL, single-use) and returns a jobId. Pair with an sseToken
 * from /api/v1/auth/sse-token to open the stream.
 */
export async function prepareGeneration(
  request: PrepareGenerationRequest
): Promise<{ jobId: string }> {
  const url = `${API_BASE_URL}/api/core/courses/generate/prepare`;

  return apiRequest(url, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

const courseApi = {
  saveCourse,
  getMyCourses,
  deleteSavedCourse,
  archiveCourse,
  unarchiveCourse,
  getGenerationJobs,
  getGenerationJob,
  cancelGenerationJob,
  checkDuplicateTopic,
  getClarifications,
  prepareGeneration,
};

export default courseApi;
